import { useEffect, useState } from 'react';

import { CompanyProfile } from '../types';
import { apiService } from '../services/apiService';

// Simple in-memory cache + pub/sub so multiple components don't refetch repeatedly
let cachedCompanies: CompanyProfile[] | null = null;
let isLoading = false;
const listeners = new Set<() => void>();

const notify = () => {
  listeners.forEach(fn => fn());
};

const loadCompanies = async () => {
  if (isLoading) return;
  isLoading = true;
  try {
    const list = await apiService.getCompanies();
    cachedCompanies = list || [];
  } catch (err) {
    console.error('useCompanies: erro ao carregar empresas', err);
    cachedCompanies = cachedCompanies || [];
  } finally {
    isLoading = false;
    notify();
  }
};

export const refreshCompanies = async () => {
  await loadCompanies();
};

export const useCompanies = () => {
  const [companies, setCompanies] = useState<CompanyProfile[]>(cachedCompanies || []);
  const [loading, setLoading] = useState<boolean>(!cachedCompanies && !isLoading);

  useEffect(() => {
    const listener = () => {
      setCompanies(cachedCompanies || []);
      setLoading(isLoading);
    };
    listeners.add(listener);

    // trigger initial load when first subscriber appears
    if (!cachedCompanies && !isLoading) {
      setLoading(true);
      loadCompanies().catch(() => setLoading(false));
    }

    // sync immediately with current cache
    listener();

    return () => {
      listeners.delete(listener);
    };
  }, []);

  const getCompanyName = (id?: string) => {
    if (!id) return '';
    const comp = (cachedCompanies || companies).find(c => c.id === id);
    if (!comp) return '';
    // prefer tradeName when available, fallback to corporateName
    return comp.tradeName || comp.corporateName || '';
  };

  return { companies, loading, refresh: refreshCompanies, getCompanyName } as const;
};

export default useCompanies;
